import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useDebounce } from '@node-space/hooks'
import PageHeader, { PageHeaderProps } from '@node-space/storybook-components/dist/PageHeader'
import {
  Box,
  EmptyState,
  EmptyStateProps,
  Illustration,
  Pagination,
  PaginationProps,
} from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { SearchAndFilter, SearchAndFilterProps } from 'components/SearchAndFilter/SearchAndFilter'
import { ActionListSkeletonLoader } from 'components/Skeletons/ActionListSkeletonLoader'
import { PAGE_SIZE } from 'pages/EmbeddedCustomers/helpers/constants'

export interface ResultsLayoutProps extends PropsWithChildren {
  isLoading: boolean
  isError: boolean
  hasResults: boolean
  hasSearchAndFilter: boolean
  emptyState: Omit<EmptyStateProps, 'Illustration' | 'testId'>
  header?: PageHeaderProps
  searchAndFilter: Omit<SearchAndFilterProps, 'testId'>
  pagination?: Omit<PaginationProps, 'testId'>
  testId: string
  refetchResults: () => void
  resetSearchAndFilters: () => void
}

export const ResultsLayout = ({
  isLoading,
  isError,
  hasResults,
  hasSearchAndFilter,
  emptyState,
  header,
  searchAndFilter,
  pagination,
  children,
  testId,
  refetchResults,
  resetSearchAndFilters,
}: ResultsLayoutProps) => {
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const hasSearchAndFilterDebounced = useDebounce(hasSearchAndFilter, 300)
  const pageSize = pagination?.rowsPerPage ?? PAGE_SIZE

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false)
    }
  }, [isLoading])

  if (isInitialLoad) {
    return (
      <ActionListSkeletonLoader
        hasPopoverMenu
        showSearchAndFilter
        showPagination
        showHeader={!!header}
        rows={pageSize}
      />
    )
  }
  if (isError) {
    return <RequestErrorStatusCard onReload={refetchResults} />
  }
  if (!isLoading && !hasResults && !hasSearchAndFilterDebounced) {
    return (
      <EmptyState
        {...emptyState}
        Illustration={<Illustration name="NoData" />}
        testId={`${testId}-empty-view`}
      />
    )
  }
  return (
    <>
      {header && <PageHeader {...header} />}
      <Box flexCol gap={16}>
        <SearchAndFilter {...searchAndFilter} testId={`${testId}-search-and-filter`} />
        {isLoading && <ActionListSkeletonLoader hasPopoverMenu rows={pageSize} />}
        {!isLoading && !hasResults && (
          <NoSearchResults clearSearchCriteria={resetSearchAndFilters} />
        )}
        {!isLoading && hasResults && children}
        {pagination && <Pagination {...pagination} testId={`${testId}-pagination`} />}
      </Box>
    </>
  )
}
